import { createSlice } from "@reduxjs/toolkit";
import { RequestResults } from "../../common/RequestStatus";
import { getAllActivityLogs, getAllEmailLogs, getAllPaymentLogs } from "./logsAsyncThunk";
import { EmailLog, LostItemLogsData, PaymentLog } from "../../../types/ActivityLog";
import { RootState } from "../../store";


interface LogsState {
    activityLogs: {
        data: LostItemLogsData[];
        counts: number;
        limit: number;
        page: number;
        status: RequestResults;
        error: string | null;
    };
    emailLogs: {
        data: EmailLog[];
        counts: number;
        limit: number;
        page: number;
        status: RequestResults;
        error: string | null;
    };
    paymentLogs: {
        data: PaymentLog[];
        counts: number;
        limit: number;
        page: number;
        status: RequestResults;
        error: string | null;
        succeededCount: number;
        refundedCount: number;
        failedCount: number;
        totalCount: number;
    };
}
const initialState: LogsState = {
    activityLogs: {
        data: [],
        counts: 0,
        limit: 10,
        page: 1,
        status: RequestResults.idle,
        error: null,
    },
    emailLogs: {
        data: [],
        counts: 0,
        limit: 10,
        page: 1,
        status: RequestResults.idle,
        error: null,
    },
    paymentLogs: {
        data: [],
        counts: 0,
        limit: 10,
        page: 1,
        status: RequestResults.idle,
        error: null,
        succeededCount: 0,
        refundedCount: 0,
        failedCount: 0,
        totalCount: 0,
    },
};

const logsSlice = createSlice({
    name: "logs",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAllActivityLogs.pending, (state) => {
                state.activityLogs.status = RequestResults.pending;
            })
            .addCase(getAllActivityLogs.fulfilled, (state, action) => {
                state.activityLogs.data = action.payload.data;
                state.activityLogs.counts = action.payload.counts;
                state.activityLogs.limit = action.payload.limit;
                state.activityLogs.status = RequestResults.success;
            })
            .addCase(getAllActivityLogs.rejected, (state, action) => {
                state.activityLogs.status = RequestResults.failed;
                state.activityLogs.error = action.payload || "Failed to fetch activity logs";
            })
            .addCase(getAllEmailLogs.pending, (state) => {
                state.emailLogs.status = RequestResults.pending;
            })
            .addCase(getAllEmailLogs.fulfilled, (state, action) => {
                state.emailLogs.data = action.payload.data;
                state.emailLogs.counts = action.payload.counts;
                state.emailLogs.limit = action.payload.limit;
                state.emailLogs.status = RequestResults.success;
            })
            .addCase(getAllEmailLogs.rejected, (state, action) => {
                state.emailLogs.status = RequestResults.failed;
                state.emailLogs.error = action.payload || "Failed to fetch email logs";
            })
            .addCase(getAllPaymentLogs.pending, (state) => {
                state.paymentLogs.status = RequestResults.pending;
            })
            .addCase(getAllPaymentLogs.fulfilled, (state, action) => {
                state.paymentLogs.data = action.payload.data;
                state.paymentLogs.counts = action.payload.counts;
                state.paymentLogs.limit = action.payload.limit;
                state.paymentLogs.succeededCount = action.payload.succeededCount;
                state.paymentLogs.refundedCount = action.payload.refundedCount;
                state.paymentLogs.failedCount = action.payload.failedCount;
                state.paymentLogs.totalCount = action.payload.totalCount;
                state.paymentLogs.status = RequestResults.success;
            })
            .addCase(getAllPaymentLogs.rejected, (state, action) => {
                state.paymentLogs.status = RequestResults.failed;
                state.paymentLogs.error = action.payload || "Failed to fetch payment logs";
            });
    },
});

export const selectLogs = (state: RootState) => state.logs;

export default logsSlice.reducer;